import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getRememberedEmail } from '../services/api';
import { AlertCircle, ArrowLeft, Lock, User, ArrowRight } from 'lucide-react';
import Logo from '../components/Logo';

const LoginPage = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  // "Recuerdame" por defecto activado: el caso comun es que el usuario
  // quiera no volver a loguearse cada vez. Si lo desmarca, el token se
  // guarda en sessionStorage y se pierde al cerrar la pestana.
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  // sessionMessage: aviso tras un cierre forzado (sesión caducada / acceso deshabilitado).
  const { login, sessionMessage } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Si en algun login anterior se guardo el email con "Recuerdame", lo
    // pre-rellenamos para que solo tenga que introducir la contrasena.
    const saved = getRememberedEmail();
    if (saved) {
      setUsername(saved);
      setRemember(true);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(username, password, remember);
      navigate('/panel', { replace: true });
    } catch (err) {
      setError(err.message || 'No se pudo iniciar sesión.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-12"
      style={{
        background:
          'radial-gradient(ellipse at top right, #E0F2FE 0%, transparent 50%), radial-gradient(ellipse at bottom left, #F1F5F9 0%, transparent 50%), #F8FAFC',
      }}
    >
      <div className="w-full max-w-md">
        <Link to="/inicio" className="inline-flex items-center gap-2 text-ink-muted hover:text-primary-dark text-sm font-medium mb-8 transition-colors">
          <ArrowLeft size={16} /> Conoce TemControl
        </Link>

        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2.5 mb-4">
            <div className="shadow-lg shadow-primary/30 rounded-[10px]">
              <Logo size={48} variant="onLight" />
            </div>
            <span className="font-display font-bold text-2xl text-ink">TemControl</span>
          </div>
          <p className="text-sm text-ink-muted">Plataforma de gestión horaria</p>
        </div>

        <div
          className="bg-white rounded-3xl p-8 md:p-10"
          style={{
            boxShadow: '0 40px 80px -20px rgba(19, 78, 74, 0.15), 0 0 0 1px rgba(19, 78, 74, 0.04)',
          }}
        >
          <h2 className="font-display text-2xl font-bold mb-1">Bienvenido de nuevo</h2>
          <p className="text-sm text-ink-muted mb-6">Introduce tus credenciales para acceder</p>

          {sessionMessage && !error && (
            <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 text-amber-800 p-3 rounded-xl mb-4">
              <AlertCircle size={16} className="flex-shrink-0" />
              <span className="text-sm">{sessionMessage}</span>
            </div>
          )}

          {error && (
            <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 p-3 rounded-xl mb-4">
              <AlertCircle size={16} className="flex-shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-ink mb-2">Usuario, email, NIF o teléfono</label>
              <div className="relative">
                <User size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="email · NIF · teléfono"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck="false"
                  className="w-full pl-11 pr-4 py-3 border border-slate-200 rounded-xl text-[0.95rem] bg-slate-50 focus:outline-none focus:border-primary focus:bg-white focus:ring-4 focus:ring-primary/10 transition"
                  required
                />
              </div>
              <div className="text-xs text-ink-muted mt-1.5">Puedes acceder con tu email, tu NIF o tu número de teléfono.</div>
            </div>

            <div>
              <label className="block text-sm font-medium text-ink mb-2">Contraseña</label>
              <div className="relative">
                <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full pl-11 pr-4 py-3 border border-slate-200 rounded-xl text-[0.95rem] bg-slate-50 focus:outline-none focus:border-primary focus:bg-white focus:ring-4 focus:ring-primary/10 transition"
                  required
                />
              </div>
            </div>

            <div className="flex items-center justify-between text-sm">
              <label className="flex items-center gap-2 text-ink-muted cursor-pointer">
                <input
                  type="checkbox"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                  className="rounded"
                />
                Recordarme
              </label>
              <Link to="/forgot-password" className="text-primary-dark hover:text-primary font-medium transition-colors">
                ¿Olvidaste tu contraseña?
              </Link>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-primary text-white py-3 rounded-xl font-semibold text-[0.95rem] flex items-center justify-center gap-2 hover:bg-primary-dark transition-all hover:shadow-lg hover:shadow-primary/30 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>Acceder <ArrowRight size={18} /></>
              )}
            </button>
          </form>

        </div>

        <p className="text-center text-ink-muted text-xs mt-6">
          © {new Date().getFullYear()} TemControl · Gestión horaria
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
